import {
  ECountry,
  EGender,
  ELanguage,
  EMaritalStatus,
  EProvince,
  getEGenderList,
  getELanguageList,
  getEProvinceList,
} from '@if/core/types'
import { CreditApplication } from '@src/data/types'
import { MonolithLoanApplicant } from '@src/data/types/MonolithLoanApplicant'

type FormApplicant = CreditApplication['applicant']

function keepText<T>(current: T, incoming: string | null | undefined): T | string {
  return incoming != null && incoming.trim() !== '' ? incoming.trim() : current
}

function keepDigits<T>(current: T, incoming: string | null | undefined): T | string {
  const digits = incoming?.replace(/\D/g, '') ?? ''
  return digits === '' ? current : digits
}

function keepEnum<TCurrent, TEnum extends number>(
  current: TCurrent,
  incoming: number | null | undefined,
  allowed: TEnum[],
): TCurrent | TEnum {
  return incoming != null && allowed.includes(incoming as TEnum) ? (incoming as TEnum) : current
}

// the backend only accepts the exact casing "Canada"; the Monolith stores "CANADA"
function keepCountry<T>(current: T, incoming: string | null | undefined): T | ECountry {
  return incoming?.trim().toLowerCase() === 'canada' ? ECountry.Canada : current
}

function keepProvince<T>(current: T, incoming: string | null | undefined): T | EProvince {
  const province = incoming?.trim().toUpperCase() as EProvince
  return province && getEProvinceList().includes(province) ? province : current
}

export function buildApplicantSeedFromMonolithLoan(
  applicant: FormApplicant,
  parentLoanApplicant: MonolithLoanApplicant,
): FormApplicant {
  const address = parentLoanApplicant.currentAddress

  return {
    ...applicant,
    firstName: keepText(applicant.firstName, parentLoanApplicant.firstName),
    middleName: keepText(applicant.middleName, parentLoanApplicant.middleName),
    lastName: keepText(applicant.lastName, parentLoanApplicant.lastName),
    birthDate: keepText(applicant.birthDate, parentLoanApplicant.birthDate),
    genderId: keepEnum<typeof applicant.genderId, EGender>(
      applicant.genderId,
      parentLoanApplicant.genderId,
      getEGenderList(),
    ),
    languageId: keepEnum<typeof applicant.languageId, ELanguage>(
      applicant.languageId,
      parentLoanApplicant.languageId,
      getELanguageList(),
    ),
    maritalStatusId: EMaritalStatus.Undefined,
    email: keepText(applicant.email, parentLoanApplicant.email),
    cellPhone: keepDigits(applicant.cellPhone, parentLoanApplicant.cellPhone),
    homePhone: keepDigits(applicant.homePhone, parentLoanApplicant.homePhone),
    currentAddress: {
      ...applicant.currentAddress,
      civicNumber: keepText(applicant.currentAddress.civicNumber, address.civicNumber),
      suite: keepText(applicant.currentAddress.suite, address.suite),
      apartment: keepText(applicant.currentAddress.apartment, address.apartment),
      street: keepText(applicant.currentAddress.street, address.street),
      city: keepText(applicant.currentAddress.city, address.city),
      postalCode: keepText(applicant.currentAddress.postalCode, address.postalCode),
      country: keepCountry(applicant.currentAddress.country, address.country),
      stateIso: keepProvince(applicant.currentAddress.stateIso, address.stateIso),
    },
  }
}
